import React from "react";

const uid = () => Math.random().toString(36).slice(2, 7);
const PIVOT_X = 60;
const PIVOT_Y = 64;

// ---------------------------------------------------------------------------
// MovingHead — sharp spot/beam fixture
// Beam has 4 layers: wide haze / mid haze / focused core / bright spine
// Source corona at beam exit simulates lens hot-spot glow
// ---------------------------------------------------------------------------
export const MovingHead = ({
    color        = "#0099FF",
    tiltAngle    = 0,
    autoTilt     = false,
    tiltRange    = 5,
    animDuration = 6,
    beamOpacity  = 0.45,
    size         = 120,
    tilt         = 0,
}) => {
    const base = tiltAngle || tilt;
    const pfx  = uid();
    const IDs  = {
        metal:   `mm-${pfx}`,
        body:    `mb-${pfx}`,
        lens:    `ml-${pfx}`,
        bHaze:   `bh-${pfx}`,
        bMid:    `bmd-${pfx}`,
        bCore:   `bc-${pfx}`,
        bSpine:  `bsp-${pfx}`,
        bSource: `bsr-${pfx}`,
    };

    const lCY     = 57;
    const lR      = 12;
    const bTop    = 69;
    const bBot    = 1800;
    const spineBot = bTop + (bBot - bTop) * 0.18;

    const animVals = `${base - tiltRange} ${PIVOT_X} ${PIVOT_Y}; ${base + tiltRange} ${PIVOT_X} ${PIVOT_Y}; ${base - tiltRange} ${PIVOT_X} ${PIVOT_Y}`;

    const bo = beamOpacity;

    return (
        <svg
            viewBox="0 0 120 300"
            width={size}
            fill="none"
            overflow="visible"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id={IDs.metal} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%"   stopColor="#72727e" />
                    <stop offset="30%"  stopColor="#404050" />
                    <stop offset="70%"  stopColor="#1e1e28" />
                    <stop offset="100%" stopColor="#0a0a12" />
                </linearGradient>
                <linearGradient id={IDs.body} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%"   stopColor="#28283a" />
                    <stop offset="50%"  stopColor="#1c1c26" />
                    <stop offset="100%" stopColor="#0e0e16" />
                </linearGradient>
                <radialGradient id={IDs.lens} cx="38%" cy="32%" r="65%" gradientUnits="objectBoundingBox">
                    <stop offset="0%"   stopColor="#ffffff" />
                    <stop offset="12%"  stopColor={color}   stopOpacity="0.95" />
                    <stop offset="55%"  stopColor={color}   stopOpacity="0.55" />
                    <stop offset="100%" stopColor="#010108" />
                </radialGradient>
                {/* Wide outer haze — very soft edges */}
                <linearGradient id={IDs.bHaze} x1="0" y1={bTop} x2="0" y2={bBot} gradientUnits="userSpaceOnUse">
                    <stop offset="0%"   stopColor={color} stopOpacity={bo * 0.28} />
                    <stop offset="22%"  stopColor={color} stopOpacity={bo * 0.09} />
                    <stop offset="55%"  stopColor={color} stopOpacity={bo * 0.02} />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
                {/* Mid haze */}
                <linearGradient id={IDs.bMid} x1="0" y1={bTop} x2="0" y2={bBot} gradientUnits="userSpaceOnUse">
                    <stop offset="0%"   stopColor={color} stopOpacity={bo * 0.55} />
                    <stop offset="28%"  stopColor={color} stopOpacity={bo * 0.16} />
                    <stop offset="65%"  stopColor={color} stopOpacity={bo * 0.04} />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
                {/* Focused core — white hot at source */}
                <linearGradient id={IDs.bCore} x1="0" y1={bTop} x2="0" y2={bBot} gradientUnits="userSpaceOnUse">
                    <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.65" />
                    <stop offset="2%"   stopColor={color}   stopOpacity={Math.min(bo * 1.2, 0.95)} />
                    <stop offset="35%"  stopColor={color}   stopOpacity={bo * 0.28} />
                    <stop offset="70%"  stopColor={color}   stopOpacity={bo * 0.07} />
                    <stop offset="100%" stopColor={color}   stopOpacity="0" />
                </linearGradient>
                {/* Spine — ultra-bright near source only */}
                <linearGradient id={IDs.bSpine} x1="0" y1={bTop} x2="0" y2={spineBot} gradientUnits="userSpaceOnUse">
                    <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.88" />
                    <stop offset="20%"  stopColor="#ffffff" stopOpacity="0.45" />
                    <stop offset="60%"  stopColor={color}   stopOpacity={bo * 0.9} />
                    <stop offset="100%" stopColor={color}   stopOpacity="0" />
                </linearGradient>
                {/* Source corona — radial bloom at lens exit */}
                <radialGradient id={IDs.bSource} cx="50%" cy="40%" r="50%" gradientUnits="objectBoundingBox">
                    <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.75" />
                    <stop offset="25%"  stopColor={color}   stopOpacity="0.55" />
                    <stop offset="60%"  stopColor={color}   stopOpacity="0.18" />
                    <stop offset="100%" stopColor={color}   stopOpacity="0" />
                </radialGradient>
            </defs>

            {/* ── STATIC: clamp + yoke ── */}
            <g>
                <line x1={PIVOT_X} y1="0" x2={PIVOT_X} y2="8" stroke="#545460" strokeWidth="2.2" />
                {/* Omega clamp body */}
                <rect x="44" y="6" width="32" height="9" rx="3" fill={`url(#${IDs.metal})`} />
                <line x1="46" y1="8.2" x2="74" y2="8.2" stroke="#888898" strokeWidth="0.5" strokeOpacity="0.45" />
                <line x1="46" y1="13.5" x2="74" y2="13.5" stroke="#2a2a38" strokeWidth="0.5" strokeOpacity="0.5" />
                {/* Safety bolt */}
                <circle cx={PIVOT_X} cy="7" r="1.5" fill="#181820" stroke="#484858" strokeWidth="0.6" />
                {/* Left yoke arm */}
                <rect x="30" y="13" width="11" height="52" rx="5.5" fill={`url(#${IDs.metal})`} />
                <line x1="31.8" y1="14.5" x2="31.8" y2="62" stroke="#808090" strokeWidth="0.7" strokeOpacity="0.35" />
                {/* Right yoke arm */}
                <rect x="79" y="13" width="11" height="52" rx="5.5" fill={`url(#${IDs.metal})`} />
                <line x1="80.8" y1="14.5" x2="80.8" y2="62" stroke="#686878" strokeWidth="0.7" strokeOpacity="0.30" />
                {/* Pivot bearings */}
                <circle cx="35.5" cy={PIVOT_Y} r="5.5" fill="#080810" stroke="#323244" strokeWidth="1.4" />
                <circle cx="35.5" cy={PIVOT_Y} r="2.8" fill={color}   opacity="0.55" />
                <circle cx="35.5" cy={PIVOT_Y} r="1.2" fill="white"  opacity="0.30" />
                <circle cx="84.5" cy={PIVOT_Y} r="5.5" fill="#080810" stroke="#323244" strokeWidth="1.4" />
                <circle cx="84.5" cy={PIVOT_Y} r="2.8" fill={color}   opacity="0.55" />
                <circle cx="84.5" cy={PIVOT_Y} r="1.2" fill="white"  opacity="0.30" />
            </g>

            {/* ── ANIMATED HEAD ── */}
            <g transform={autoTilt ? undefined : `rotate(${base}, ${PIVOT_X}, ${PIVOT_Y})`}>
                {autoTilt && (
                    <animateTransform
                        attributeName="transform" type="rotate"
                        values={animVals} dur={`${animDuration}s`} repeatCount="indefinite"
                        calcMode="spline"
                        keySplines="0.45 0 0.55 1; 0.45 0 0.55 1"
                        keyTimes="0;0.5;1"
                    />
                )}

                {/* ── BEAM (drawn first, behind housing) ── */}
                {/* Layer 1: wide outer haze */}
                <path
                    d={`M ${PIVOT_X - 24} ${bTop} L ${PIVOT_X - 90} ${bBot} L ${PIVOT_X + 90} ${bBot} L ${PIVOT_X + 24} ${bTop} Z`}
                    fill={`url(#${IDs.bHaze})`}
                />
                {/* Layer 2: mid haze */}
                <path
                    d={`M ${PIVOT_X - 13} ${bTop} L ${PIVOT_X - 46} ${bBot} L ${PIVOT_X + 46} ${bBot} L ${PIVOT_X + 13} ${bTop} Z`}
                    fill={`url(#${IDs.bMid})`}
                />
                {/* Layer 3: focused core */}
                <path
                    d={`M ${PIVOT_X - 5.5} ${bTop} L ${PIVOT_X - 16} ${bBot} L ${PIVOT_X + 16} ${bBot} L ${PIVOT_X + 5.5} ${bTop} Z`}
                    fill={`url(#${IDs.bCore})`}
                />
                {/* Layer 4: bright spine */}
                <path
                    d={`M ${PIVOT_X - 2} ${bTop} L ${PIVOT_X - 3.5} ${spineBot} L ${PIVOT_X + 3.5} ${spineBot} L ${PIVOT_X + 2} ${bTop} Z`}
                    fill={`url(#${IDs.bSpine})`}
                />
                {/* Source corona ellipse */}
                <ellipse cx={PIVOT_X} cy={bTop + 1} rx="26" ry="11"
                    fill={`url(#${IDs.bSource})`} />

                {/* ── Body housing ── */}
                <rect x="34" y="16" width="52" height="50" rx="5" fill={`url(#${IDs.body})`} />
                {/* Top highlight */}
                <rect x="34" y="16" width="52" height="1.5" rx="0.75" fill="#505060" opacity="0.55" />
                {/* Panel seam */}
                <line x1="35.5" y1="30" x2="84.5" y2="30" stroke="#0c0c14" strokeWidth="0.7" strokeOpacity="0.9" />
                <line x1="35.5" y1="31" x2="84.5" y2="31" stroke="#30303e" strokeWidth="0.4" strokeOpacity="0.5" />
                {/* Vent slots */}
                <line x1="76" y1="20" x2="83" y2="20" stroke="#08080f" strokeWidth="1.4" strokeOpacity="0.95" />
                <line x1="76" y1="23" x2="83" y2="23" stroke="#08080f" strokeWidth="1.4" strokeOpacity="0.95" />
                <line x1="76" y1="26" x2="83" y2="26" stroke="#08080f" strokeWidth="1.4" strokeOpacity="0.95" />

                {/* ── Lens assembly ── */}
                <circle cx={PIVOT_X} cy={lCY} r={lR + 5.5} fill="#07070e" />
                <circle cx={PIVOT_X} cy={lCY} r={lR + 5.5} fill="none" stroke="#3a3a4c" strokeWidth="1.4" />
                <circle cx={PIVOT_X} cy={lCY} r={lR + 3}   fill="none" stroke="#1c1c2c" strokeWidth="0.8" />
                <circle cx={PIVOT_X} cy={lCY} r={lR}       fill={`url(#${IDs.lens})`} />
                <circle cx={PIVOT_X} cy={lCY} r={lR * 0.68} fill="none" stroke={color} strokeWidth="0.5" strokeOpacity="0.28" />
                <circle cx={PIVOT_X} cy={lCY} r="4"        fill="white" opacity="0.98" />
                <circle cx={PIVOT_X} cy={lCY} r="2"        fill="white" opacity="1" />
                <ellipse cx={PIVOT_X - 4.5} cy={lCY - 4.5} rx="3" ry="1.6"
                    fill="white" opacity="0.22"
                    transform={`rotate(-32,${PIVOT_X - 4.5},${lCY - 4.5})`} />
            </g>
        </svg>
    );
};


// ---------------------------------------------------------------------------
// WashHead — wide-angle LED wash fixture
// Wider, more diffuse beam with LED array bloom
// ---------------------------------------------------------------------------
export const WashHead = ({
    color        = "#0055CC",
    tiltAngle    = 0,
    autoTilt     = false,
    tiltRange    = 8,
    animDuration = 9,
    beamOpacity  = 0.3,
    size         = 120,
    tilt         = 0,
}) => {
    const base = tiltAngle || tilt;
    const pfx  = uid();
    const IDs  = {
        metal:   `wm-${pfx}`,
        body:    `wb-${pfx}`,
        lens:    `wl-${pfx}`,
        bloom:   `wbl-${pfx}`,
        bHaze:   `wbh-${pfx}`,
        bMid:    `wbm-${pfx}`,
        bCore:   `wbc-${pfx}`,
        bSource: `wbs-${pfx}`,
    };

    const bodyX  = 27;
    const bodyW  = 66;
    const lCY    = 54;
    const lR     = 17;
    const bTop   = 71;
    const bBot   = 1800;

    const animVals = `${base - tiltRange} ${PIVOT_X} ${PIVOT_Y}; ${base + tiltRange} ${PIVOT_X} ${PIVOT_Y}; ${base - tiltRange} ${PIVOT_X} ${PIVOT_Y}`;

    const bo = beamOpacity;

    return (
        <svg
            viewBox="0 0 120 300"
            width={size}
            fill="none"
            overflow="visible"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id={IDs.metal} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%"   stopColor="#686878" />
                    <stop offset="35%"  stopColor="#3c3c4c" />
                    <stop offset="70%"  stopColor="#1e1e2c" />
                    <stop offset="100%" stopColor="#0c0c14" />
                </linearGradient>
                <linearGradient id={IDs.body} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%"   stopColor="#242434" />
                    <stop offset="50%"  stopColor="#1a1a24" />
                    <stop offset="100%" stopColor="#10101a" />
                </linearGradient>
                <radialGradient id={IDs.lens} cx="50%" cy="42%" r="58%" gradientUnits="objectBoundingBox">
                    <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.75" />
                    <stop offset="28%"  stopColor={color}   stopOpacity="0.90" />
                    <stop offset="68%"  stopColor={color}   stopOpacity="0.48" />
                    <stop offset="100%" stopColor={color}   stopOpacity="0.08" />
                </radialGradient>
                {/* LED array bloom — soft glow over LED panel */}
                <radialGradient id={IDs.bloom} cx="50%" cy="50%" r="50%" gradientUnits="objectBoundingBox">
                    <stop offset="0%"   stopColor={color}  stopOpacity="0.35" />
                    <stop offset="55%"  stopColor={color}  stopOpacity="0.12" />
                    <stop offset="100%" stopColor={color}  stopOpacity="0" />
                </radialGradient>
                {/* Wide outer haze — very diffuse wash */}
                <linearGradient id={IDs.bHaze} x1="0" y1={bTop} x2="0" y2={bBot} gradientUnits="userSpaceOnUse">
                    <stop offset="0%"   stopColor={color} stopOpacity={bo * 0.38} />
                    <stop offset="20%"  stopColor={color} stopOpacity={bo * 0.14} />
                    <stop offset="50%"  stopColor={color} stopOpacity={bo * 0.04} />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
                {/* Mid wash */}
                <linearGradient id={IDs.bMid} x1="0" y1={bTop} x2="0" y2={bBot} gradientUnits="userSpaceOnUse">
                    <stop offset="0%"   stopColor={color} stopOpacity={bo * 0.70} />
                    <stop offset="30%"  stopColor={color} stopOpacity={bo * 0.24} />
                    <stop offset="65%"  stopColor={color} stopOpacity={bo * 0.06} />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
                {/* Core — wide for wash, white hot at source */}
                <linearGradient id={IDs.bCore} x1="0" y1={bTop} x2="0" y2={bBot} gradientUnits="userSpaceOnUse">
                    <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.55" />
                    <stop offset="3%"   stopColor={color}   stopOpacity={Math.min(bo * 1.1, 0.92)} />
                    <stop offset="32%"  stopColor={color}   stopOpacity={bo * 0.30} />
                    <stop offset="70%"  stopColor={color}   stopOpacity={bo * 0.07} />
                    <stop offset="100%" stopColor={color}   stopOpacity="0" />
                </linearGradient>
                {/* Source corona */}
                <radialGradient id={IDs.bSource} cx="50%" cy="38%" r="50%" gradientUnits="objectBoundingBox">
                    <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.65" />
                    <stop offset="22%"  stopColor={color}   stopOpacity="0.48" />
                    <stop offset="60%"  stopColor={color}   stopOpacity="0.14" />
                    <stop offset="100%" stopColor={color}   stopOpacity="0" />
                </radialGradient>
            </defs>

            {/* ── STATIC ── */}
            <g>
                <line x1={PIVOT_X} y1="0" x2={PIVOT_X} y2="8" stroke="#505058" strokeWidth="2.2" />
                <rect x="42" y="6" width="36" height="9" rx="3" fill={`url(#${IDs.metal})`} />
                <line x1="44" y1="8.2" x2="76" y2="8.2" stroke="#848494" strokeWidth="0.5" strokeOpacity="0.42" />
                <circle cx={PIVOT_X} cy="7" r="1.5" fill="#181820" stroke="#484858" strokeWidth="0.6" />
                {/* Yoke arms — wider than MovingHead */}
                <rect x="26" y="13" width="13" height="52" rx="6.5" fill={`url(#${IDs.metal})`} />
                <line x1="27.8" y1="14.5" x2="27.8" y2="62" stroke="#787888" strokeWidth="0.7" strokeOpacity="0.32" />
                <rect x="81" y="13" width="13" height="52" rx="6.5" fill={`url(#${IDs.metal})`} />
                <line x1="82.8" y1="14.5" x2="82.8" y2="62" stroke="#686878" strokeWidth="0.7" strokeOpacity="0.28" />
                {/* Pivot bearings */}
                <circle cx="32.5" cy={PIVOT_Y} r="5.5" fill="#08080e" stroke="#303042" strokeWidth="1.4" />
                <circle cx="32.5" cy={PIVOT_Y} r="2.8" fill={color}   opacity="0.50" />
                <circle cx="32.5" cy={PIVOT_Y} r="1.2" fill="white"  opacity="0.28" />
                <circle cx="87.5" cy={PIVOT_Y} r="5.5" fill="#08080e" stroke="#303042" strokeWidth="1.4" />
                <circle cx="87.5" cy={PIVOT_Y} r="2.8" fill={color}   opacity="0.50" />
                <circle cx="87.5" cy={PIVOT_Y} r="1.2" fill="white"  opacity="0.28" />
            </g>

            {/* ── ANIMATED HEAD ── */}
            <g transform={autoTilt ? undefined : `rotate(${base}, ${PIVOT_X}, ${PIVOT_Y})`}>
                {autoTilt && (
                    <animateTransform
                        attributeName="transform" type="rotate"
                        values={animVals} dur={`${animDuration}s`} repeatCount="indefinite"
                        calcMode="spline"
                        keySplines="0.45 0 0.55 1; 0.45 0 0.55 1"
                        keyTimes="0;0.5;1"
                    />
                )}

                {/* ── BEAM (behind housing) ── */}
                {/* Layer 1: very wide outer haze */}
                <path
                    d={`M ${PIVOT_X - 32} ${bTop} L ${PIVOT_X - 195} ${bBot} L ${PIVOT_X + 195} ${bBot} L ${PIVOT_X + 32} ${bTop} Z`}
                    fill={`url(#${IDs.bHaze})`}
                />
                {/* Layer 2: mid wash */}
                <path
                    d={`M ${PIVOT_X - 18} ${bTop} L ${PIVOT_X - 100} ${bBot} L ${PIVOT_X + 100} ${bBot} L ${PIVOT_X + 18} ${bTop} Z`}
                    fill={`url(#${IDs.bMid})`}
                />
                {/* Layer 3: core wash */}
                <path
                    d={`M ${PIVOT_X - 10} ${bTop} L ${PIVOT_X - 52} ${bBot} L ${PIVOT_X + 52} ${bBot} L ${PIVOT_X + 10} ${bTop} Z`}
                    fill={`url(#${IDs.bCore})`}
                />
                {/* Source corona */}
                <ellipse cx={PIVOT_X} cy={bTop + 1} rx="34" ry="13"
                    fill={`url(#${IDs.bSource})`} />

                {/* ── Body housing ── */}
                <rect x={bodyX} y="16" width={bodyW} height="48" rx="6" fill={`url(#${IDs.body})`} />
                <rect x={bodyX} y="16" width={bodyW} height="1.5" rx="0.75" fill="#4c4c5e" opacity="0.52" />
                <line x1={bodyX + 1} y1="30" x2={bodyX + bodyW - 1} y2="30" stroke="#0c0c14" strokeWidth="0.7" strokeOpacity="0.85" />

                {/* ── LED array panel ── */}
                <rect x="36" y="36" width="48" height="20" rx="3" fill="#080810" stroke="#1a1a28" strokeWidth="0.7" />
                {/* LED bloom over array */}
                <ellipse cx={PIVOT_X} cy="46" rx="24" ry="10" fill={`url(#${IDs.bloom})`} />
                {/* Individual LEDs */}
                {[
                    { x: 48, y: 41 }, { x: 60, y: 41 }, { x: 72, y: 41 },
                    { x: 48, y: 50 }, { x: 60, y: 50 }, { x: 72, y: 50 },
                ].map((pt, idx) => (
                    <g key={idx}>
                        <circle cx={pt.x} cy={pt.y} r="3"   fill={color} fillOpacity="0.35" />
                        <circle cx={pt.x} cy={pt.y} r="1.5" fill="white" opacity="0.60" />
                    </g>
                ))}

                {/* ── Lens housing ── */}
                <circle cx={PIVOT_X} cy={lCY} r={lR + 5} fill="#07070c" />
                <circle cx={PIVOT_X} cy={lCY} r={lR + 5} fill="none" stroke="#363648" strokeWidth="1.4" />
                <circle cx={PIVOT_X} cy={lCY} r={lR + 3} fill="none" stroke="#1c1c2a" strokeWidth="0.8" />
                <circle cx={PIVOT_X} cy={lCY} r={lR}     fill={`url(#${IDs.lens})`} />
                <circle cx={PIVOT_X} cy={lCY} r={lR * 0.62} fill="none" stroke={color} strokeWidth="0.5" strokeOpacity="0.30" />
                <circle cx={PIVOT_X} cy={lCY} r="5"      fill="white" opacity="0.65" />
                <circle cx={PIVOT_X} cy={lCY} r="2.5"    fill="white" opacity="1" />
                <ellipse cx={PIVOT_X - 5.5} cy={lCY - 5.5} rx="3.2" ry="1.8"
                    fill="white" opacity="0.20"
                    transform={`rotate(-32,${PIVOT_X - 5.5},${lCY - 5.5})`} />
            </g>
        </svg>
    );
};


// ---------------------------------------------------------------------------
// TrussBar — 4-chord box truss viewed in slight perspective
// ---------------------------------------------------------------------------
export const TrussBar = ({ className = "", width = 1600 }) => {
    const pfx    = uid();
    const topId  = `tb-top-${pfx}`;
    const sideId = `tb-side-${pfx}`;
    const botId  = `tb-bot-${pfx}`;

    const TL = 4;
    const TR = 8;
    const BL = 30;
    const BR = 34;
    const chordH = 5;
    const bay    = 32;
    const bays   = Math.floor(width / bay);

    return (
        <svg
            viewBox={`0 0 ${width} 44`}
            width="100%"
            height={44}
            preserveAspectRatio="none"
            className={className}
            style={{ display: "block" }}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id={topId} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%"   stopColor="#585864" />
                    <stop offset="60%"  stopColor="#383842" />
                    <stop offset="100%" stopColor="#242430" />
                </linearGradient>
                <linearGradient id={sideId} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%"   stopColor="#303038" />
                    <stop offset="100%" stopColor="#18181e" />
                </linearGradient>
                <linearGradient id={botId} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%"   stopColor="#242430" />
                    <stop offset="100%" stopColor="#141418" />
                </linearGradient>
            </defs>

            {Array.from({ length: bays }).map((_, i) => {
                const x0 = i * bay;
                const x1 = (i + 1) * bay;
                const isEven = i % 2 === 0;
                return (
                    <g key={i}>
                        <line
                            x1={isEven ? x0 : x1} y1={isEven ? TL + chordH : BL}
                            x2={isEven ? x1 : x0} y2={isEven ? BL : TL + chordH}
                            stroke="#2c2c38" strokeWidth="1.6" strokeLinecap="round"
                        />
                        <line
                            x1={isEven ? x0 : x1} y1={isEven ? TR + chordH : BR}
                            x2={isEven ? x1 : x0} y2={isEven ? BR : TR + chordH}
                            stroke="#222230" strokeWidth="1.4" strokeLinecap="round"
                        />
                        <line
                            x1={isEven ? x0 : x1} y1={TL + chordH / 2}
                            x2={isEven ? x1 : x0} y2={TR + chordH / 2}
                            stroke="#383848" strokeWidth="1.2" strokeLinecap="round"
                        />
                        <circle cx={x0} cy={TL + chordH / 2} r="2.2" fill="#2e2e3c" />
                        <circle cx={x0} cy={BL + chordH / 2} r="2.2" fill="#202028" />
                    </g>
                );
            })}
            <circle cx={bays * bay} cy={TL + chordH / 2} r="2.2" fill="#2e2e3c" />
            <circle cx={bays * bay} cy={BL + chordH / 2} r="2.2" fill="#202028" />

            <rect x="0" y={BL} width={width} height={chordH} rx="2.5" fill={`url(#${botId})`} />
            <rect x="0" y={BR} width={width} height={chordH - 1} rx="2" fill={`url(#${sideId})`} opacity="0.7" />
            <rect x="0" y={TL} width={width} height={chordH} rx="2.5" fill={`url(#${topId})`} />
            <line x1="0" y1={TL + 0.8} x2={width} y2={TL + 0.8}
                stroke="#686874" strokeWidth="0.7" strokeOpacity="0.5" />
            <rect x="0" y={TR} width={width} height={chordH - 1} rx="2" fill={`url(#${sideId})`} opacity="0.8" />

            <rect x="0"         y={TL} width="5" height={BR + chordH - TL} rx="2" fill="#32323e" />
            <rect x={width - 5} y={TL} width="5" height={BR + chordH - TL} rx="2" fill="#28282e" />
        </svg>
    );
};
